import type { EventData } from '../types/index';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import {
    PsHttpService,
    type PatSnapCommonResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { getLlmModelHeader, LLM_FUNCTION_ID } from '@eureka/utils/llm-model';

export interface ChatJobRequest {
    user_query: string;
}
export interface ChatJobResponse {
    company_scout_id: string;
}

export interface SendChatMessageRequest {
    company_scout_id: string;
    append_message: string;
}

export interface ConfirmMessageRequest {
    confirm_type: string;
    remark: string;
}

export interface HistoryRequest {
    query: string;
}

export interface HistoryItem {
    id: string;
    business_id: string;
    title: string;
}

export type HistoryResponse = HistoryItem[];

export interface ChatApi {
    createCompanySearchJob(data: ChatJobRequest): Promise<ChatJobResponse>;
    getJobStream(data: {
        company_scout_id: string;
    }): PsHttpSSE<EventData>;
    sendChatMessage(data: SendChatMessageRequest): Promise<void>;
    confirmMessage(data: ConfirmMessageRequest): Promise<void>;
    getHistory(data: HistoryRequest): Promise<HistoryResponse>;
}

export const ChatApi = Symbol('ChatApi');

@injectable()
export class ChatApiImpl extends PsHttpService implements ChatApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company-scout`,
            headers: {
                'X-API-Version': '1.0',
                ...getLlmModelHeader(LLM_FUNCTION_ID.COMPANY_SEARCH),
            },
        };
    }

    /**
     * 创建一个TRIZ任务
     */
    createCompanySearchJob(data: ChatJobRequest) {
        return this.post<PatSnapCommonResponseData<ChatJobResponse>>({
            url: '/start',
            data,
        }).then((res) => {
            return res.data as ChatJobResponse;
        });
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    /**
     * 获取任务的流
     */
    getJobStream(data: {
        company_scout_id: string;
    }) {
        return this._factorySsePsHttp<EventData>()
            .init({
                url: `${this.options.baseURL}/stream`,
                data,
                hasFinalMessage: () => true,
            })
            .setFetchHeaders(this.options.headers)
    }

    /**
     * 发送对话消息
     */
    sendChatMessage(data: SendChatMessageRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/append',
            data,
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 用户确认问题
     */
    confirmMessage(data: ConfirmMessageRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/confirm',
            data,
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 获取历史记录
     */
    getHistory(data: HistoryRequest) {
        return this.post<PatSnapCommonResponseData<HistoryResponse>>({
            url: '/history',
            data,
        }).then((res) => {
            return res.data as HistoryResponse;
        });
    }
}
